import {
  Component,
  inject,
  input,
  output,
  effect,
  signal,
} from "@angular/core";

import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatMenuModule } from "@angular/material/menu";
import { MatButtonModule } from "@angular/material/button";
import { ApiService } from "../../services/api.service";
import { IOpenAiModel } from "../../interfaces/i-open-ai-model";
import { IOpenAiServices } from "../../interfaces/i-openai-services";
import { AlertEnum } from "../../shared/alert/alert.enum";
import { AlertService } from "../../shared/alert/alert.service";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatDividerModule } from "@angular/material/divider";
import { AiChatService } from "../../pages/ai-chat/services/ai-chat.service";
import { AutomationStateService } from "../../pages/ai-chat/services/automation-state.service";
import { ChatSessionService } from "../../pages/ai-chat/services/chat-session.service";
import { ReactiveFormsModule } from "@angular/forms";
import { MatInputModule } from "@angular/material/input";
import { InstructionBuilderService } from "../../pages/automation/instructions/instruction-builder/services/instruction-builder.service";
import { UtilitiesService } from "../../services/utilities.service";
import { Router } from "@angular/router";
import { RoutePath } from "../../enums/api.enum";
import { ConversationStarterService } from "../../pages/ai-chat/services/conversation-starter.service";
import { environment } from "../../../environments/environment";
import { MultiSessionService } from "../../services/multi-session.service";

@Component({
  selector: "ai-models",
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatTooltipModule,
    MatMenuModule,
    MatButtonModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatDividerModule
],
  templateUrl: "./ai-models.component.html",
  styleUrl: "./ai-models.component.scss",
})
export class AiModelsComponent {
  selectedModel: IOpenAiModel = { accountName: "", model: "" };
  services: IOpenAiServices[] = [];
  model: IOpenAiModel = { accountName: "", model: "" };
  starterConversationModel = output<IOpenAiModel>();
  // isQuickEdit is true when used in the chat interface for quick model changes
  // false when used in session manager where all changes are saved at once
  isQuickEdit = input<boolean>(false);
  // isCreateSessionOrInstruction is true when used in the Create Session dialog or Create Instruction page.
  // this flag is used to prevent using either selectedSession or Instructions to populate the model
  // and to use the first model in the array instead
  isCreateSessionOrInstruction = input<boolean>(false);
  modelSelected = output<IOpenAiModel>();
  temperatureChanged = output<number>();
  topPChanged = output<number>();
  aiChatService = inject(AiChatService);
  automationStateService = inject(AutomationStateService);
  multiSessionService = inject(MultiSessionService);
  isStarterConversation = input<boolean>(false);

  // Signal to keep track of whether the currently selected Session is submitting and so we can use this value in the html 
  // to disable the text input area or not.
  isCurrentSessionSubmitting = signal<boolean>(false);

  // Model selection functionality
  #openAiModels = signal<IOpenAiModel[]>([]);
  #filteredModels = signal<IOpenAiModel[]>([]);
  #services = signal<IOpenAiServices[]>([]);

  // Method to group models by accountName for the dropdown
  groupedModels = signal<{ accountName: string; models: string[] }[]>([]);

  #apiService = inject(ApiService);
  #alertService = inject(AlertService);
  #chatSessionService = inject(ChatSessionService);
  #instructionBuilderService = inject(InstructionBuilderService);
  #utilitiesService = inject(UtilitiesService);
  #conversationStarterService = inject(ConversationStarterService);

  #router = inject(Router);

  // Updated constructor to examine the current route and set the selected model accordingly
  constructor() {
    // Load models once during initialization
    this.loadModels();

    // Create an effect that specifically tracks changes and sets the model based on the current route
    effect(() => {
      // Explicitly access the selected session or instruction to track it and get the model from
      const selectedSession = this.#chatSessionService.selectedSession();
      const sessionModel = selectedSession?.model;
      const currentInstruction = this.#instructionBuilderService.instruction();
      const currentUrl = this.#router.url;

      // Check which route we're on and set the selected model accordingly
      if (currentUrl.includes(RoutePath.EditInstruction) || currentUrl.includes(RoutePath.CloneInstruction)) {
        // For edit or clone instruction routes, use the current instruction model
        if (currentInstruction && currentInstruction.details.model) {
          this.selectedModel = currentInstruction.details.model;
        } else if (this.#openAiModels().length > 0) {
          this.selectedModel = this.#openAiModels()[0] || { accountName: "", model: "" };
        }
      } else if (currentUrl.includes(RoutePath.Session) && !this.isCreateSessionOrInstruction()) {
        // For edit session route, use the session model
        if (sessionModel && sessionModel.accountName && sessionModel.model) {
          this.selectedModel = sessionModel;
        } else if (this.#openAiModels().length > 0) {
          this.selectedModel = this.#openAiModels()[0] || { accountName: "", model: "" };
        }
      } else if (this.#conversationStarterService.isStartConversation() && !this.isCreateSessionOrInstruction()) {
        // Guard with !isCreateSessionOrInstruction() so that opening the create-session dialog from the
        // start-session page uses the environment default (else branch) rather than the conversation starter's model.
        
        // TODO - TECH DEBT:
        // We will want to get default models from an API call and at most, implement fallback logic beyond it.

        // Use the model already stored in the conversation signal (set by default config or a previous selection),
        // falling back to the environment default model (same logic as the else branch below).
        // Assign directly to avoid triggering selectModel() which would write back to conversation() 
        // and cause this effect to re-run in a loop.
        const conversationModel = this.#conversationStarterService.conversation()?.model;
        const models = this.#openAiModels();
        const defaultFallback = models.find((m: IOpenAiModel) =>
          m.model.toLowerCase().trim() === environment.defaultModelConfig.model.toLowerCase().trim() &&
          m.accountName.toLowerCase().trim() === environment.defaultModelConfig.accountName.toLowerCase().trim()
        ) ?? models[0];
        this.selectedModel = (conversationModel?.accountName && conversationModel?.model)
          ? conversationModel
          : (defaultFallback ?? { accountName: "", model: "" });
      } else {
        // For create-session, create-instruction, or any other unmatched route,
        // prefer the environment default model over the arbitrary first model in the list.
        const models = this.#openAiModels();
        const defaultModel = models.find((m: IOpenAiModel) =>
          m.model.toLowerCase().trim() === environment.defaultModelConfig.model.toLowerCase().trim() &&
          m.accountName.toLowerCase().trim() === environment.defaultModelConfig.accountName.toLowerCase().trim()
        ) ?? models[0];
        this.selectedModel = defaultModel ?? { accountName: "", model: "" };
      }
    });

    effect(() => {
      this.isCurrentSessionSubmitting.set(this.#chatSessionService.selectedSessionId() ? this.multiSessionService.isSessionStreaming(this.#chatSessionService.selectedSessionId()) : false);
    });
  }

  // Filter models based on search input
  filterModels(event: KeyboardEvent): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();

    if (!searchTerm) {
      this.#filteredModels.set(this.#openAiModels());
    } else {
      const filtered = this.#openAiModels().filter(
        (model) =>
          model.accountName.toLowerCase().includes(searchTerm) ||
          model.model.toLowerCase().includes(searchTerm)
      );
      this.#filteredModels.set(filtered);
    }

    this.updateGroupedModels();
  }

  // Update the grouped models for display
  updateGroupedModels(): void {
    const grouped: { [key: string]: string[] } = {};

    this.#filteredModels().forEach((model) => {
      if (!grouped[model.accountName]) {
        grouped[model.accountName] = [];
      }
      grouped[model.accountName].push(model.model);
    });

    const result = Object.keys(grouped).map((accountName) => ({
      accountName,
      models: grouped[accountName],
    }));

    this.groupedModels.set(result);
  }

  // Load models from API service
  loadModels(): void {
    this.#apiService.getModels().subscribe({
      next: (response) => {
        this.#services.set(response);

        // Convert services to OpenAI models
        const models: IOpenAiModel[] = [];
        response.forEach((service) => {
          service.modelNames.forEach((modelName) => {
            models.push({
              accountName: service.accountName,
              model: modelName,
            });
          });
        });

        this.#openAiModels.set(models);
        this.#filteredModels.set(models);

        this.updateGroupedModels();

        // For create-session/instruction context, emit the initial model selection once
        // so the parent form stays in sync with what this component displays.
        if (this.isCreateSessionOrInstruction()) {
          const defaultModel = models.find((m: IOpenAiModel) =>
            m.model.toLowerCase().trim() === environment.defaultModelConfig.model.toLowerCase().trim() &&
            m.accountName.toLowerCase().trim() === environment.defaultModelConfig.accountName.toLowerCase().trim()
          ) ?? models[0];
          if (defaultModel) {
            this.modelSelected.emit(defaultModel);
          }
        }
      },
      error: (error: any) => {
        this.#alertService.showAlert({
          message: `Failed to get models.`,
          type: AlertEnum.error,
        });
      },
    });
  }

  // Select a model
  /**
   * Checks if a model name is a GPT-5 model
   * @param modelName The name of the model to check
   * @returns boolean indicating if it's a GPT-5 model
   */
  isGpt5Model(modelName: string): boolean {
    return this.#utilitiesService.isGpt5Model(modelName);
  }

  selectModel(accountName: string, model: string): void {
    const newModel = { accountName, model };
    this.selectedModel = newModel;

    // Check if the model is GPT-5 and update temperature and topP accordingly
    const isGpt5 = this.isGpt5Model(model);

    if (!this.isStarterConversation()) {
        // Update the session with the new model
        const session = this.#chatSessionService.selectedSession();

        if (session && this.isQuickEdit() && !this.isCreateSessionOrInstruction()) {
          const updatedSession = { ...session, model: newModel };

          // If GPT-5 is selected, update temperature and topP parameters
          if (isGpt5) {
            // Get recommended temperature and topP values for the model
            const recommendedTemperature = this.#utilitiesService.getRecommendedTemperature(model);
            const recommendedTopP = this.#utilitiesService.getRecommendedTopP(model);

            // Update session with these values
            updatedSession.temperature = recommendedTemperature;
            updatedSession.topP = recommendedTopP;
          }

          // Update in the chat sessions array
          this.#chatSessionService.chatSessions.update((sessions) => {
            if (!sessions) return sessions;
            return sessions.map((s) => (s.id === session.id ? updatedSession : s));
          });

          // Persist the session to the backend with the updated model
          this.#apiService.persistSession(updatedSession).subscribe({
            next: (response) => {
              // Update the session with the response data
              this.#chatSessionService.chatSessions.update((sessions) => {
                if (!sessions) return sessions;
                return sessions.map((s) =>
                  s.id === response.id ? { ...s, model: newModel } : s
                );
              });

              // Generate system message about model change
              const updatedModelMessage = this.aiChatService.handleAiConfigMismatch(
                session,
                newModel,
                updatedSession.temperature,
                updatedSession.topP
              );

              // If there are changes, create a system message
              if (updatedModelMessage.length > 0) {
                this.aiChatService.createConfigChangeSystemMessage(
                  session,
                  updatedModelMessage,
                  session.id
                );
              }
            },
            error: (error) => {
              this.#alertService.showAlert({
                message: `Failed to update model.`,
                type: AlertEnum.error,
              });
            }
          });
        } else {
          // When used in session manager or create/edit instruction, just emit the event without making API calls
          // This prevents duplicate API calls when session manager later saves all changes
          this.modelSelected.emit(this.selectedModel);

          // If GPT-5 is selected, emit temperature and topP changes
          if (this.isGpt5Model(model)) {
            // Get recommended temperature and topP values for the model
            const recommendedTemperature = this.#utilitiesService.getRecommendedTemperature(model);
            const recommendedTopP = this.#utilitiesService.getRecommendedTopP(model);

            // Emit the changes to be captured by the parent component
            this.temperatureChanged.emit(recommendedTemperature);
            this.topPChanged.emit(recommendedTopP);
          }
        }
      } else {
        // In the conversation starter context, update the shared service signal directly
        // so ConversationStarterComponent always has the current model in conversation().
        // Also emit the output so AiChatPromptComponent.selectedModel stays in sync —
        // executePrompt() reads that local field when building the conversation payload.
        this.#conversationStarterService.conversation.update(convo =>
          convo ? { ...convo, model: newModel } : convo
        );
        this.starterConversationModel.emit(newModel);
      }
    }

  // Check if a model is currently selected
  isSelectedModel(accountName: string, model: string): boolean {
    const selected = this.selectedModel;
    return selected.accountName === accountName && selected.model === model;
  }

  // Get tooltip text for the model selector button
  getModelTooltip(): string {
    const selected = this.selectedModel;
    if (selected.accountName && selected.model) {
      return `Selected model: ${selected.accountName} - ${selected.model}`;
    } else {
      return "Select model";
    }
  }
}
